const MVP_NAV = [
  ["See", "/mvp/see"],
  ["Hear", "/mvp/hear"],
  ["Feel", "/mvp/interact"],
  ["Engage", "/mvp/talk"],
];

const MVP_STATUS = "MVP status — currently in development";
export const TECH_NAV = [
  ["Real-Time AI Video", "/technology/real-time-ai-video"],
  ["AI Voice", "/technology/ai-voice"],
  ["Conversational AI", "/technology/conversational-ai"],
  ["Photorealistic AI Characters", "/technology/photorealistic-ai-characters"],
  ["Emotional Responses", "/technology/emotional-responses"],
  ["Facial Expressions", "/technology/facial-expressions"],
  ["Natural Movement", "/technology/natural-movement"],
  ["Interactive AI Companions", "/technology/interactive-ai-companions"],
];

const ROADMAP_NAV = [
  ["Level 1 · MVP", "/roadmap/real-time-videochat-mvp"],
  ["Level 2 · Richer Interaction", "/roadmap/level-2-richer-interaction"],
  ["Level 3 · Advanced Interaction", "/roadmap/level-3-advanced-interaction"],
  ["Commercial Platform", "/roadmap/commercial-platform"],
  ["Humanoid AI", "/roadmap/humanoid-ai"],
  ["AI Avatar Technology", "/roadmap/ai-avatar-technology"],
];

const BUSINESS_MODEL_NAV = [
  ["Subscriptions", "/business-model/subscriptions"],
  ["Premium Experiences", "/business-model/premium-interactive-experiences"],
  ["Live AI Entertainment", "/business-model/live-ai-entertainment"],
  ["Premium AI Characters", "/business-model/premium-ai-characters"],
  ["Future B2B", "/business-model/future-b2b"],
];

const MARKET_NAV = [
  ["Artificial Intelligence", "/market/artificial-intelligence"],
  ["Interactive Entertainment", "/market/interactive-entertainment"],
  ["Real-Time Video", "/market/real-time-video"],
  ["Virtual Companions", "/market/virtual-companions"],
];

const NO_MARKET_STATS_NOTE =
  "We deliberately do not quote market-size figures. Category sizes and growth rates are published by specialist research firms, and unsourced numbers help no one. This page explains qualitatively why the category matters to AIROBOTCAMSTUDIO.";

const PROPOSED_MODEL_STATUS =
  "This is a proposed future commercial model. AIROBOTCAMSTUDIO LTD currently has no customers, no paying users and no revenue, and no pricing, conversion, demand or commercial traction has been validated. Nothing on this page is a claim of an existing or launched commercial product.";


export const DETAIL_PAGES = {
  "/mvp/see": {
    tag: "MVP Feature",
    title: "SEE",
    subtitle: "Photorealistic AI characters designed for live visual interaction.",
    status: MVP_STATUS,
    metaTitle: "SEE — Photorealistic AI Characters | AIROBOTCAMSTUDIO MVP",
    metaDesc:
      "AIROBOTCAMSTUDIO is developing photorealistic AI characters designed for live, face-to-face visual interaction. MVP currently in development.",
    sections: [
      {
        paragraphs: [
          "Most AI interaction today happens through text on a screen. We are developing something different: photorealistic AI characters you can actually see — digital humans with a natural facial presence, designed from the start for real-time visual interaction.",
          "The intended experience is closer to a video call than a chat window. You see the character, the character responds visually, and the interaction feels face-to-face rather than typed.",
        ],
      },
      {
        heading: "The intended experience",
        bullets: [
          "Photorealistic digital humans rather than cartoon avatars",
          "Natural facial presence with believable, expressive faces",
          "Expressive facial animation that responds during conversation",
          "Real-time visual interaction in a video-call style interface",
          "Distinct character identity — appearance, style and personality",
          "Responsive expressions that react to what you say",
          "Natural eye contact where technically possible",
          "Designed for live, face-to-face AI interaction",
        ],
      },
      {
        paragraphs: [
          "The MVP is designed to prove this visual experience first: one photorealistic character, one conversation, in real time. Everything else builds on that foundation.",
        ],
      },
    ],
    cta: { label: "Explore the Demo", href: "/#demo" },
    back: { label: "Back to MVP", href: "/#mvp" },
    nav: MVP_NAV,
  },

  "/mvp/hear": {
    tag: "MVP Feature",
    title: "HEAR",
    subtitle: "Natural voice and conversational intelligence.",
    status: MVP_STATUS,
    metaTitle: "HEAR — Natural AI Voice | AIROBOTCAMSTUDIO MVP",
    metaDesc:
      "AIROBOTCAMSTUDIO is developing natural AI voice interaction and conversational intelligence designed for real-time conversation. MVP currently in development.",
    sections: [
      {
        paragraphs: [
          "Voice is the fastest, most natural way people communicate. We are developing AI voice interaction that aims to feel like talking to a present, attentive character — not issuing commands to a machine.",
        ],
      },
      {
        heading: "The intended experience",
        bullets: [
          "Real-time voice interaction — speak, and the character responds",
          "Natural speech output rather than robotic text-to-speech",
          "A distinct voice identity for each AI character",
          "Conversational intelligence — personality, context and memory within the session",
          "Free-flowing dialogue rather than scripted question-and-answer",
          "Conversational pacing — natural pauses, rhythm and turn-taking",
          "Listening and response designed to feel attentive",
          "Emotional tone in the voice where supported",
          "Low-latency interaction as a core development goal",
        ],
      },
      {
        paragraphs: [
          "Our goal is for the voice to carry the same identity you see on screen — consistent, characterful and believable within a live conversation. Our characters are simulated personalities: engaging by design, without claims of consciousness or genuine emotion.",
        ],
      },
    ],
    cta: { label: "Explore the Demo", href: "/#demo" },
    back: { label: "Back to MVP", href: "/#mvp" },
    nav: MVP_NAV,
  },

  "/mvp/talk": {
    tag: "MVP Feature",
    title: "ENGAGE",
    subtitle: "Measurable one-to-one interaction.",
    status: MVP_STATUS,
    metaTitle: "ENGAGE — One-to-One AI Interaction | AIROBOTCAMSTUDIO MVP",
    metaDesc:
      "AIROBOTCAMSTUDIO's Level 1 is built around measurable one-to-one interaction: one person, one photorealistic AI character, one live session. MVP currently in development.",
    sections: [
      {
        paragraphs: [
          "Level 1 is built around one-to-one interaction: one person, one photorealistic AI character, one live session. ENGAGE is where the experience becomes measurable — real sessions, real responses, real user behaviour.",
        ],
      },
      {
        heading: "The intended experience",
        bullets: [
          "One-to-one live sessions with a single AI character",
          "Direct, personal interaction rather than broadcast content",
          "Session-based engagement — joining, interacting and returning",
          "Free-flowing conversation carried by voice, expression and movement",
          "Designed to be measurable — real session behaviour will inform product validation",
        ],
      },
      {
        paragraphs: [
          "Engagement is the proof point the MVP is built to test. No engagement, retention or conversion metrics exist yet — measuring them honestly is the purpose of Level 1 validation.",
        ],
      },
    ],
    cta: { label: "Explore the Demo", href: "/#demo" },
    back: { label: "Back to MVP", href: "/#mvp" },
    nav: MVP_NAV,
  },

  "/mvp/interact": {
    tag: "MVP Feature",
    title: "FEEL",
    subtitle: "Expression, movement and persistent personality.",
    status: MVP_STATUS,
    metaTitle: "FEEL — Expression & Movement | AIROBOTCAMSTUDIO MVP",
    metaDesc:
      "AIROBOTCAMSTUDIO is developing simulated expressions, natural movement and persistent character personality for AI companions. MVP currently in development.",
    sections: [
      {
        paragraphs: [
          "A still face breaks the illusion of presence. We are developing a visual behaviour layer that keeps the character responsive during interaction — expressions, small movements and reactions that respond to the conversation.",
        ],
      },
      {
        heading: "The intended experience",
        bullets: [
          "Facial expressions that respond to the flow of conversation",
          "Visual reactions timed to what you say",
          "Natural head movement rather than a locked camera pose",
          "Natural idle motion — subtle presence between responses",
          "Conversational gestures where supported",
          "Emotional-expression simulation matched to tone",
          "Synchronized visual response alongside voice output",
          "Responsive character behaviour across the whole session",
          "Persistent personality — the same character identity across sessions",
        ],
      },
      {
        paragraphs: [
          "These expressions and emotions are simulated — a designed layer of responsive visual behaviour, not felt emotion. We believe this honesty matters, and it shapes how we build.",
        ],
      },
    ],
    cta: { label: "Explore the Demo", href: "/#demo" },
    back: { label: "Back to MVP", href: "/#mvp" },
    nav: MVP_NAV,
  },

  "/roadmap/ai-avatar-technology": {
    tag: "Foundation Technology",
    title: "AI Avatar Technology",
    subtitle: "Building photorealistic AI characters for natural real-time interaction.",
    status: "In development",
    metaTitle: "AI Avatar Technology — Roadmap | AIROBOTCAMSTUDIO",
    metaDesc:
      "How AIROBOTCAMSTUDIO is building photorealistic AI avatar technology: character creation, facial animation, lip sync and real-time delivery. Currently in development.",
    sections: [
      {
        heading: "What we are building",
        bullets: [
          "Photorealistic digital humans",
          "Facial animation",
          "Lip synchronization",
          "Identity consistency across sessions",
          "Facial expressions",
          "Eye and head movement",
          "Natural idle movement",
          "Conversational reactions",
          "Voice-character synchronization",
          "Real-time character delivery",
          "Personality layer",
          "Conversational AI integration",
        ],
      },
      {
        heading: "How we intend to build it",
        steps: [
          "Character creation — defining appearance, identity and personality",
          "Facial rigging and animation",
          "Speech and lip-sync integration",
          "Conversational AI connection",
          "Real-time response pipeline",
          "Behaviour and expression layer",
          "Performance optimisation",
          "Testing and iteration",
        ],
      },
      {
        heading: "Technology approach",
        paragraphs: [
          "The platform may use a combination of real-time rendering, generative AI models and streaming technologies. We are currently evaluating options, and the final production stack will be selected during MVP development based on quality, latency and cost. No final technology vendor has been confirmed.",
        ],
      },
      {
        heading: "Expected MVP outcome",
        paragraphs: [
          "A photorealistic AI character capable of participating in a real-time conversational video experience with synchronized speech and responsive visual behaviour.",
        ],
      },
    ],
    note: "This describes a development target, not a completed capability. The MVP is currently in development.",
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Roadmap", href: "/#roadmap" },
    nav: ROADMAP_NAV,
  },

  "/roadmap/real-time-videochat-mvp": {
    tag: "Roadmap · Level 1",
    title: "Level 1 — Real-Time Videochat MVP",
    subtitle: "The current commercial build: the first working interactive AI companion experience.",
    status: "In development",
    metaTitle: "Real-Time Videochat MVP — Roadmap | AIROBOTCAMSTUDIO",
    metaDesc:
      "The AIROBOTCAMSTUDIO MVP development plan: the intended interaction pipeline, MVP components and development phases. Currently in development.",
    sections: [
      {
        heading: "The intended interaction pipeline",
        flow: [
          "User",
          "Microphone / input",
          "Speech recognition",
          "Conversational AI",
          "Response generation",
          "Voice output",
          "Lip sync + character animation",
          "Real-time video experience",
        ],
      },
      {
        heading: "MVP components",
        bullets: [
          "User session interface",
          "Microphone / audio input",
          "Conversational input layer",
          "Conversational AI",
          "Personality and context",
          "AI voice",
          "Lip sync",
          "Facial animation",
          "Avatar / video rendering",
          "Session management",
          "Latency optimisation",
          "Safety and moderation where applicable",
          "Backend / API integration",
        ],
      },
      {
        heading: "Development phases",
        steps: [
          "Phase 1 — basic conversational pipeline",
          "Phase 2 — voice interaction",
          "Phase 3 — avatar integration",
          "Phase 4 — lip-sync and expression response",
          "Phase 5 — real-time performance optimisation",
          "Phase 6 — closed testing / beta",
          "Phase 7 — iteration toward commercial release",
        ],
      },
      {
        heading: "Level 1 scope — the current commercial build",
        bullets: [
          "Photorealistic AI character + real-time video (in development)",
          "Natural voice + conversational AI (in development)",
          "Facial reactions and movement (in development)",
          "Signup, demo, subscription and payments (planned within Level 1)",
          "Beta analytics, moderation and feedback (planned within Level 1)",
        ],
      },
      {
        heading: "MVP priorities and controls",
        bullets: [
          "Latency — interaction should feel immediate",
          "Realism — believable character, voice and expression",
          "Session stability — reliable real-time sessions",
          "Unit economics — infrastructure cost per streamed minute kept visible",
          "Controls — moderation, disclosure, privacy and payments",
        ],
      },
      {
        heading: "Expected MVP outcome",
        paragraphs: [
          "A working prototype demonstrating conversational interaction with a photorealistic AI character through a video-call style interface.",
        ],
      },
    ],
    note: "The MVP is currently in development. The SEIS round is focused on completing and validating Level 1. No completion is claimed until verified through testing.",
    cta: { label: "View Concept Demo", href: "/#demo" },
    back: { label: "Back to Roadmap", href: "/#roadmap" },
    nav: ROADMAP_NAV,
  },

  "/roadmap/level-2-richer-interaction": {
    tag: "Roadmap · Level 2",
    title: "Level 2 — Richer Interaction",
    subtitle: "User-triggered actions, reactions and richer interaction.",
    status: "Next — follows Level 1",
    metaTitle: "Level 2 Richer Interaction — Roadmap | AIROBOTCAMSTUDIO",
    metaDesc:
      "AIROBOTCAMSTUDIO Level 2: user-triggered actions, reactions and richer interaction. The next stage after the Level 1 MVP — not built yet.",
    sections: [
      {
        paragraphs: [
          "Level 2 builds on a validated Level 1. Where Level 1 proves the core experience — a live photorealistic character you can see, hear and talk with in real time — Level 2 is intended to deepen it, so the character does more than converse.",
          "This stage has not been built. It begins only after the Level 1 MVP is working and validated with real users.",
        ],
      },
      {
        heading: "What Level 2 is intended to add",
        bullets: [
          "User-triggered actions — the character responds to what you ask it to do",
          "Reactions — richer, more varied responses to the flow of a session",
          "Richer interaction — sessions that go beyond question-and-answer conversation",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [
          "Level 2 is a next-stage target, not a current capability. All Level 2 development remains within the AI video companion platform — the same product to which SEIS capital is exclusively allocated.",
        ],
      },
    ],
    cta: { label: "View Level 1 MVP", href: "/roadmap/real-time-videochat-mvp" },
    back: { label: "Back to Roadmap", href: "/#roadmap" },
    nav: ROADMAP_NAV,
  },

  "/roadmap/level-3-advanced-interaction": {
    tag: "Roadmap · Level 3",
    title: "Level 3 — Advanced Interaction",
    subtitle: "Advanced movement, environment interaction and premium formats.",
    status: "Future",
    metaTitle: "Level 3 Advanced Interaction — Roadmap | AIROBOTCAMSTUDIO",
    metaDesc:
      "AIROBOTCAMSTUDIO Level 3: advanced movement, environment interaction and premium formats. A future stage of the AI video companion platform — not built yet.",
    sections: [
      {
        paragraphs: [
          "Level 3 is the future stage where interaction is intended to become visibly richer: characters with more advanced movement, awareness of their presented environment, and premium session formats built on top of the core experience.",
          "This stage is future work. It follows Level 2 and depends on everything before it working and being validated.",
        ],
      },
      {
        heading: "What Level 3 is intended to add",
        bullets: [
          "Advanced movement — fuller, more natural character motion",
          "Environment interaction — the character relating to its presented setting",
          "Premium formats — higher-value session types for premium members",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [
          "Level 3 is a future target, not a current capability. It remains part of the AI video companion platform — humanoid robotics is a separate, longer-term vision and is not what SEIS capital funds.",
        ],
      },
    ],
    cta: { label: "View Level 1 MVP", href: "/roadmap/real-time-videochat-mvp" },
    back: { label: "Back to Roadmap", href: "/#roadmap" },
    nav: ROADMAP_NAV,
  },

  "/roadmap/commercial-platform": {
    tag: "Roadmap · Commercial Stage",
    title: "Commercial Platform",
    subtitle: "The proposed path from a working MVP to a sustainable commercial product.",
    status: "Future",
    metaTitle: "Commercial Platform — Roadmap | AIROBOTCAMSTUDIO",
    metaDesc:
      "AIROBOTCAMSTUDIO's proposed path from MVP to a commercial platform: validation, paid usage and subscription validation, controlled acquisition. Future stage — the commercial platform does not exist today.",
    sections: [
      {
        paragraphs: [
          "The commercial platform is the stage at which the technology built and validated through Levels 1 to 3 is intended to become a paid, self-sustaining product.",
          "This stage is future work. The commercial platform does not exist today — the Level 1 MVP is currently in development, and every step below depends on the one before it.",
        ],
      },
      {
        heading: "The proposed path",
        flow: ["MVP", "Validation", "Paid usage / subscription validation", "Controlled acquisition", "Commercial platform"],
      },
      {
        heading: "What each step means",
        steps: [
          "MVP — build the working Level 1 real-time AI videochat experience. This stage is currently in development.",
          "Validation — test the MVP with real users and measure honestly whether the experience works for them.",
          "Paid usage / subscription validation — introduce the proposed paid access models described in our business model pages, only once the product is ready.",
          "Controlled acquisition — grow the user base gradually and deliberately, rather than scaling ahead of the technology.",
          "Commercial platform — a stable, paid product with the infrastructure, support and operations to run it properly.",
        ],
      },
      {
        heading: "Go-to-market — launch narrow, measure before scaling",
        steps: [
          "Closed beta — recruit targeted early users",
          "Conversion — validate the path from early access to paid usage",
          "Retention — measure repeat sessions and monthly usage",
          "Paid acquisition — scale only after CAC and retention are measurable",
        ],
      },
      {
        paragraphs: [
          "These go-to-market stages are validation objectives. Marketing spend is intended to increase only once usable unit economics are visible — none of these metrics exist yet.",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [
          "Level 1 is in development; Levels 2 and 3 and the commercial stage are future work. AIROBOTCAMSTUDIO LTD currently has no customers, no paying users and no revenue, and the commercial models described on this website are proposed, not live. Nothing on this page is a claim of existing traction, validated demand or a launched product.",
        ],
      },
    ],
    cta: { label: "Explore the Technology", href: "/#technology" },
    back: { label: "Back to Roadmap", href: "/#roadmap" },
    nav: ROADMAP_NAV,
  },

  "/roadmap/humanoid-ai": {
    tag: "Future Vision · Level 4+",
    title: "Humanoid AI",
    subtitle: "From digital AI characters to embodied intelligent interaction.",
    status: "Long-term vision",
    metaTitle: "Humanoid AI — Long-Term Vision | AIROBOTCAMSTUDIO",
    metaDesc:
      "AIROBOTCAMSTUDIO's long-term vision: exploring how conversational AI and digital character identity could one day extend into physical humanoid systems. Long-term vision only — not the current Level 1 product and not part of the current £150,000 MVP fundraising allocation.",
    sections: [
      {
        paragraphs: [
          "AIROBOTCAMSTUDIO's long-term vision is to explore how conversational AI, digital character identity and real-time interaction could eventually extend beyond the screen into physical humanoid systems.",
          "The idea is continuity of identity: the same character you see and talk to on screen could, one day, have a physical presence. This is a research direction for a later stage of the company — not a product claim.",
        ],
      },
      {
        heading: "Potential future research areas",
        bullets: [
          "Embodied conversational AI",
          "Humanoid platforms",
          "AI character identity carried across digital and physical forms",
          "Voice interaction",
          "Visual perception",
          "Robotic movement",
          "Expression and gesture",
          "Human-centred interaction",
          "Digital-to-physical AI identity",
          "Integration between conversational intelligence and physical humanoid systems",
        ],
      },
      {
        heading: "Important clarification",
        paragraphs: [
          "Humanoid AI is a long-term vision. It is not the current Level 1 product, and it is not part of the current SEIS-funded commercial build: the £150,000 round in preparation is directed at the digital AI interaction platform.",
          "AIROBOTCAMSTUDIO is not currently selling or manufacturing humanoid robots, and no finished physical robot product exists. The company's current development and fundraising focus is the digital AI interaction platform. Any future robotics work would be subject to separate planning and funding.",
        ],
      },
    ],
    cta: { label: "View Current MVP", href: "/#mvp" },
    back: { label: "Back to Roadmap", href: "/#roadmap" },
    nav: ROADMAP_NAV,
  },

  "/investment/use-of-funds/ai-development": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "AI Development",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — AI Development | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support AI development at AIROBOTCAMSTUDIO: conversational AI integration, orchestration and specialist engineering.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "AI development covers the intelligence layer of the product — the systems that let a character understand, decide and respond during a live conversation.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "Conversational AI integration",
          "AI orchestration",
          "Model / API integration",
          "Context systems",
          "Character personality systems",
          "Speech / AI integration",
          "Backend AI services",
          "Development tools",
          "Specialist engineering",
          "Technical validation",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "The quality of conversation is the product. Investment in this area directly determines whether interacting with a character feels natural, responsive and worth returning to.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/use-of-funds/avatar-technology": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "Avatar Technology",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — Avatar Technology | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support avatar technology at AIROBOTCAMSTUDIO: character creation, facial animation and lip sync.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "Avatar technology covers the visual character — the photorealistic digital human you see on screen, and the systems that animate it convincingly in real time.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "Character creation",
          "Digital human development",
          "Facial rigging",
          "Facial animation",
          "Lip sync",
          "Expression systems",
          "Character consistency",
          "Real-time character integration",
          "Animation tooling",
          "Specialist licensing / development where required",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "Presence is visual first. If the character does not look and move believably, nothing else matters — this is the area that makes the experience feel face-to-face.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/use-of-funds/real-time-video-infrastructure": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "Real-Time Video Infrastructure",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — Real-Time Video Infrastructure | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support real-time video infrastructure at AIROBOTCAMSTUDIO: streaming, hosting and latency optimisation.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "Real-time video infrastructure covers everything required to deliver a live, low-latency video experience between a user and an AI character — reliably, and at scale.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "Real-time communication infrastructure",
          "Video / audio streaming",
          "Session infrastructure",
          "Hosting / cloud",
          "Bandwidth",
          "Backend",
          "APIs",
          "Latency optimisation",
          "Scalable architecture",
          "Monitoring",
          "Security",
          "Infrastructure testing",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "A real-time experience lives or dies on latency and reliability. Infrastructure is what turns a working demo into a product that stays responsive for every user, every session.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/use-of-funds/product-development": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "Product Development",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — Product Development | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support product development at AIROBOTCAMSTUDIO: frontend, backend, architecture and MVP engineering.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "Product development covers the engineering of the actual product — the interface, systems and features users touch, built around the AI and avatar technology.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "Frontend",
          "Backend",
          "Architecture",
          "Account / session systems",
          "Product features",
          "AI / avatar integration",
          "MVP engineering",
          "Deployment",
          "Internal tools",
          "Development maintenance",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "Strong technology still needs a strong product around it. This is the work that turns capability into something people can actually sign up for, use and enjoy.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/use-of-funds/ux-ui": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "UX/UI",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — UX/UI | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support UX/UI at AIROBOTCAMSTUDIO: interaction design, onboarding, accessibility and user journeys.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "UX/UI covers how the product feels to use — the design of the interaction itself, from first visit to live conversation.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "User experience",
          "Video interaction interface",
          "Onboarding",
          "Navigation",
          "Mobile / responsive",
          "Accessibility",
          "Prototype testing",
          "Design system",
          "User journey optimisation",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "For a product built around presence and conversation, the interface is the experience. Good design makes the technology approachable; bad design makes it invisible.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/use-of-funds/testing": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "Testing",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — Testing | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support testing at AIROBOTCAMSTUDIO: performance, latency, avatar sync and closed beta testing.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "Testing covers the structured verification of everything we build — technically, visually and with real users — before and during release.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "Technical testing",
          "Integration testing",
          "Browser / device testing",
          "Performance testing",
          "Latency testing",
          "AI response testing",
          "Avatar sync testing",
          "Usability testing",
          "Closed beta testing",
          "Bug fixing",
          "Reliability / security testing",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "A real-time AI product has many moving parts that must stay in sync. Disciplined testing is what makes the difference between an impressive demo and a dependable product.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/use-of-funds/marketing": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "Marketing",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — Marketing | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support marketing at AIROBOTCAMSTUDIO: positioning, launch preparation and beta recruitment.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "Marketing covers how the product is positioned and introduced — preparing for launch and finding the first users who will shape it.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "Product positioning",
          "Launch preparation",
          "Website / product communication",
          "Early user acquisition",
          "Beta recruitment",
          "Content",
          "Investor / company communications",
          "Market validation",
          "Initial digital marketing",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "Even a strong product needs a clear story and an early audience. Marketing spend at this stage is deliberately proportionate — the priority remains building the product.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated. Marketing is kept proportionate to early-stage development.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/use-of-funds/operations": {
    tag: "Use of Funds · £150,000 MVP Round",
    title: "Operations",
    status: "Planned — round in preparation",
    metaTitle: "Use of Funds — Operations | AIROBOTCAMSTUDIO",
    metaDesc:
      "How the proposed £150,000 round may support operations at AIROBOTCAMSTUDIO: software, professional services and company administration.",
    sections: [
      {
        heading: "Purpose",
        paragraphs: [
          "Operations covers the essentials that keep the company running properly while the product is being built — nothing glamorous, all necessary.",
        ],
      },
      {
        heading: "What the funds may support",
        bullets: [
          "Essential software",
          "Cloud / SaaS",
          "Professional services",
          "Accounting",
          "Legal / compliance",
          "Company administration",
          "Contractor / specialist support",
          "Essential operational infrastructure",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "Clean operations protect the company and its investors — proper accounting, compliance and administration are the foundation everything else sits on.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Planned. The £150,000 fundraising round is in preparation and no funds have been raised or allocated.",
        ],
      },
      {
        heading: "Planned use of funds",
        paragraphs: [
          "Allocations will be defined as the round progresses and will be shared with prospective investors through the company's investor communications. No exact figures are published at this stage.",
        ],
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/seis-advance-assurance": {
    tag: "Investment",
    title: "SEIS Advance Assurance",
    subtitle: "Our approach to SEIS — transparent, careful and in preparation.",
    status: "Application being prepared",
    metaTitle: "SEIS Advance Assurance | AIROBOTCAMSTUDIO",
    metaDesc:
      "AIROBOTCAMSTUDIO LTD is preparing an application for SEIS Advance Assurance. What Advance Assurance is, how the process works, and what it means for investors.",
    sections: [
      {
        heading: "What SEIS Advance Assurance is",
        paragraphs: [
          "The Seed Enterprise Investment Scheme (SEIS) is a UK government programme that offers tax reliefs to individual investors who subscribe for new shares in qualifying early-stage companies.",
          "Advance Assurance is a service from HMRC that allows a company to ask, before investment is taken, whether it is likely to qualify for SEIS based on the information supplied. It gives founders and investors an indication of eligibility before a round proceeds.",
          "Advance Assurance is not SEIS approval, not a guarantee of tax relief, and not an endorsement of the company or its prospects. Investor tax treatment always depends on individual circumstances and on the company continuing to meet the applicable requirements.",
        ],
      },
      {
        heading: "How the process works",
        steps: [
          "Company preparation — confirming structure, eligibility and readiness",
          "Investor interest — gathering indicative support for the round",
          "Supporting documentation — business plan, forecasts and company details",
          "HMRC application — submitting the Advance Assurance request",
          "HMRC review — HMRC may ask questions or request further detail",
          "HMRC decision — an indication of whether the company is likely to qualify based on the information supplied",
          "Investment & share issue — the round proceeds and shares are issued to investors",
          "SEIS compliance process — post-investment compliance statements and investor certificates, subject to applicable requirements",
        ],
      },
      {
        heading: "Our current position",
        paragraphs: [
          "AIROBOTCAMSTUDIO LTD is preparing an application for SEIS Advance Assurance. No application has yet been decided, and no SEIS status is claimed. Any reference to SEIS on this website is subject to HMRC's review and to the scheme's applicable requirements.",
        ],
        bullets: [
          "SEIS eligibility is subject to HMRC approval and applicable requirements",
          "Advance Assurance indicates a company is likely to qualify based on information supplied — it is not a guarantee",
          "Investor tax treatment depends on individual circumstances",
          "Nothing on this website constitutes tax, legal or investment advice",
        ],
      },
    ],
    externalLinks: [
      {
        label: "View official HMRC SEIS guidance",
        href: "https://www.gov.uk/guidance/venture-capital-schemes-apply-for-advance-assurance",
      },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/investment/crowdcube": {
    tag: "Investment",
    title: "Crowdcube",
    subtitle: "A future fundraising channel under consideration — explained honestly.",
    status: "Future fundraising channel under consideration",
    metaTitle: "Crowdcube Fundraising | AIROBOTCAMSTUDIO",
    metaDesc:
      "AIROBOTCAMSTUDIO LTD is considering a future Crowdcube fundraising campaign. How the Crowdcube process typically works for companies, and our current position.",
    sections: [
      {
        heading: "Our current position",
        paragraphs: [
          "AIROBOTCAMSTUDIO LTD is preparing for a possible future Crowdcube fundraising campaign as part of its wider investment strategy. Crowdcube has not accepted, approved or endorsed AIROBOTCAMSTUDIO, and no campaign is live or scheduled.",
        ],
      },
      {
        heading: "How the Crowdcube process typically works",
        steps: [
          "Register / apply — the company submits an application to the platform",
          "Initial review — the platform assesses whether the company is a fit",
          "Create the investment opportunity — pitch, materials and terms are prepared",
          "Verification — the platform's due diligence and verification checks",
          "Campaign / fundraising — the pitch runs publicly for a set period",
          "Legal & compliance process — completion requirements and documentation",
          "Round completion — funds are transferred and shares are issued",
        ],
      },
      {
        heading: "Important clarification",
        paragraphs: [
          "Registering interest with AIROBOTCAMSTUDIO is not an investment commitment and is separate from registering or investing through Crowdcube. If a Crowdcube campaign proceeds in the future, it would be announced on this website with full details at that time.",
        ],
      },
    ],
    externalLinks: [
      { label: "Visit Crowdcube", href: "https://www.crowdcube.com" },
      { label: "Learn about raising on Crowdcube", href: "https://www.crowdcube.com/explore/raising" },
    ],
    cta: { label: "Register Investor Interest", href: "/#contact" },
    back: { label: "Back to Investment", href: "/#investment" },
  },

  "/technology/real-time-ai-video": {
    tag: "Technology",
    title: "Real-Time AI Video",
    subtitle: "Moving AI interaction beyond text into live visual conversation.",
    status: "In development",
    metaTitle: "Real-Time AI Video | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's real-time AI video capability is intended to work: live visual conversation with a photorealistic AI character. In development.",
    sections: [
      {
        paragraphs: [
          "Real-time AI video is the layer that puts a present, responsive character on screen — moving AI interaction beyond text boxes into live visual conversation. This page explains how the capability is intended to work; the MVP feature pages describe what the experience is intended to feel like.",
        ],
      },
      {
        heading: "The intended architecture",
        bullets: [
          "Live video-style interaction in a familiar call format",
          "Real-time character presentation on screen",
          "Audio input captured from the user's microphone",
          "Conversational processing of what was said",
          "AI-generated response",
          "Voice output in the character's voice",
          "Synchronized avatar response — lips, face and movement",
          "Session management across the conversation",
          "Latency optimisation at every stage",
          "Responsive visual interaction throughout",
        ],
      },
      {
        heading: "The intended flow",
        flow: [
          "User",
          "Voice / input",
          "Conversational AI",
          "Response",
          "Voice + character animation",
          "Real-time visual experience",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "In development. The commercial system is not yet operational — this architecture describes what we are building toward, and each stage will be verified through testing before release.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/technology/ai-voice": {
    tag: "Technology",
    title: "AI Voice",
    subtitle: "Giving each AI character a natural conversational voice.",
    status: "In development",
    metaTitle: "AI Voice | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's AI voice capability is intended to work: speech input, voice identity and synchronized character speech. In development.",
    sections: [
      {
        paragraphs: [
          "Voice is what turns a silent character into a conversational partner. The AI voice layer is intended to handle both directions of speech — understanding what you say and speaking the character's reply in a natural, characterful voice.",
        ],
      },
      {
        heading: "How it is intended to work",
        bullets: [
          "Speech input from the user",
          "Speech recognition / conversational input processing",
          "AI-generated dialogue for the response",
          "Text-to-speech or equivalent voice generation",
          "Character-specific voice identity",
          "Conversational pacing — natural rhythm and pauses",
          "Interruption and turn-taking as a development objective",
          "Synchronization with avatar animation",
          "Latency optimisation for real-time feel",
          "Emotional tone where technically supported",
        ],
      },
      {
        heading: "How voice connects",
        flow: [
          "User speaks",
          "AI understands input",
          "Response generated",
          "Character speaks",
          "Face / lips respond",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "In development. Voice capabilities will be validated through closed testing before any public release.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/technology/conversational-ai": {
    tag: "Technology",
    title: "Conversational AI",
    subtitle: "The intelligence layer behind the interaction.",
    status: "In development",
    metaTitle: "Conversational AI | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's conversational AI layer is intended to work: contextual responses, character personality and session continuity. In development.",
    sections: [
      {
        paragraphs: [
          "Conversational AI is the intelligence layer behind the whole experience — the system that interprets what you say and decides how the character responds. Everything visible and audible builds on top of it.",
        ],
      },
      {
        heading: "How it is intended to work",
        bullets: [
          "Interpreting user input in context",
          "Generating contextual responses",
          "Conversational continuity within sessions",
          "Character personality expressed through language",
          "Response style consistent with each character",
          "Contextual information carried across the conversation",
          "Conversation management and turn-taking",
          "Connection with voice output",
          "Connection with avatar behaviour",
          "Safety and moderation where applicable",
        ],
      },
      {
        paragraphs: [
          "Important clarity: conversational AI does not imply consciousness or genuine human understanding. Our characters generate contextually appropriate language — an engineered, honest simulation of conversation, presented as exactly that.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "In development. The conversational layer is being built and tuned as part of the MVP.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/technology/photorealistic-ai-characters": {
    tag: "Technology",
    title: "Photorealistic AI Characters",
    subtitle: "Digital humans designed for expressive real-time interaction.",
    status: "In development",
    metaTitle: "Photorealistic AI Characters | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's photorealistic AI characters are intended to be built: visual identity, facial rigging, lip sync and real-time animation. In development.",
    image: {
      src: "/assets/posters/tech-characters.jpg",
      alt: "Selene — a photorealistic AI character concept, cinematic studio portrait, experience in development",
      caption: "Concept visual — AI-generated fictional character · experience in development",
    },
    sections: [
      {
        paragraphs: [
          "The character is the face of the experience — literally. We are developing photorealistic digital humans designed from the start for expressive, real-time interaction rather than static display.",
        ],
      },
      {
        heading: "How they are intended to be built",
        bullets: [
          "Character creation — appearance, identity and style",
          "Visual identity that stays recognisably consistent",
          "Realistic skin and facial detail",
          "Facial rigging for animation control",
          "Lip synchronization with voice output",
          "Expressions matched to conversation",
          "Eye and head movement",
          "Character consistency across sessions",
          "Real-time animation performance",
          "Integration with conversational AI and voice",
        ],
      },
      {
        paragraphs: [
          "The concept visuals on this website show the intended quality bar: believable digital humans with natural presence — not cartoon avatars, and not real people. All characters are AI-generated and fictional.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "In development. Character technology is Roadmap Chapter 01 and is actively being built toward the MVP.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/technology/emotional-responses": {
    tag: "Technology",
    title: "Emotional Responses",
    subtitle: "Simulated behavioural responses designed to make interaction feel more natural.",
    status: "In development",
    metaTitle: "Emotional Responses | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's simulated emotional responses are intended to work — generated expressions and tone, honestly explained. In development.",
    sections: [
      {
        paragraphs: [
          "AIROBOTCAMSTUDIO's intended characters may visually and vocally respond to conversational context using simulated emotional behaviour. The purpose is simple: interaction feels flat when a character never reacts, so we are designing a response layer that reacts appropriately to the tone of the conversation.",
        ],
      },
      {
        heading: "What that may look like",
        bullets: [
          "Smiling in response to humour or warmth",
          "Concern when the conversation turns serious",
          "Surprise at unexpected input",
          "Enthusiasm in upbeat moments",
          "Changes in vocal tone",
          "Changes in expression",
          "Context-sensitive visual reactions",
        ],
      },
      {
        heading: "An honest clarification",
        paragraphs: [
          "These are computationally generated, simulated responses — selected and rendered by software, not felt by anyone.",
          "AI characters do not possess human emotions. Emotional responses refer to generated expressions, voice and behaviour designed to support a more natural interaction.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "In development. The response layer is planned as part of the MVP's behaviour system and will be tuned through testing.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/technology/facial-expressions": {
    tag: "Technology",
    title: "Facial Expressions",
    subtitle: "Visual reactions synchronized with conversation.",
    status: "In development",
    metaTitle: "Facial Expressions | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's facial expression system is intended to work: animation, lip movement and context-driven expressions. In development.",
    sections: [
      {
        paragraphs: [
          "A face that never moves breaks the sense of presence instantly. The facial expression system is intended to keep the character's face expressive and synchronized with the conversation at all times.",
        ],
      },
      {
        heading: "How it is intended to work",
        bullets: [
          "Facial animation driven by the conversation",
          "Lip movement synchronized with speech",
          "Smiling and warm expressions",
          "Eyebrow movement for emphasis and reaction",
          "Eye movement and gaze",
          "Natural blinking",
          "Conversational reactions in real time",
          "Smooth expression transitions",
          "Speech synchronization",
          "Context-driven expressions",
        ],
      },
      {
        heading: "The intended pipeline",
        flow: ["Conversation", "Response context", "Expression selection", "Facial animation"],
      },
      {
        heading: "Current status",
        paragraphs: [
          "In development. Expression quality will be iterated extensively during MVP testing.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/technology/natural-movement": {
    tag: "Technology",
    title: "Natural Movement",
    subtitle: "Reducing the static feeling of traditional digital avatars.",
    status: "In development",
    metaTitle: "Natural Movement | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's natural movement system is intended to work: idle motion, head movement and conversational gestures. In development.",
    sections: [
      {
        paragraphs: [
          "Traditional digital avatars often feel like statues with moving mouths. Natural movement is the layer intended to fix that — keeping the character gently, believably in motion the way a real person is on a video call.",
        ],
      },
      {
        heading: "How it is intended to work",
        bullets: [
          "Natural head movement",
          "Eye movement and gaze shifts",
          "Blinking",
          "Breathing-style idle animation",
          "Subtle posture changes",
          "Subtle body movement",
          "Conversational gestures where supported",
          "Smooth transitions between expressions",
          "Synchronization with speech",
        ],
      },
      {
        paragraphs: [
          "The objective is believable character presence — not perfect replication of human movement. Small, natural, continuous motion matters more than theatrical animation.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "In development. Movement behaviour is planned as part of the MVP's character system.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/technology/interactive-ai-companions": {
    tag: "Technology",
    title: "Interactive AI Companions",
    subtitle: "Bringing the technology layers together into one experience.",
    status: "MVP currently in development",
    metaTitle: "Interactive AI Companions | AIROBOTCAMSTUDIO Technology",
    metaDesc:
      "How AIROBOTCAMSTUDIO's technology layers combine into the intended interactive AI companion experience. MVP currently in development.",
    sections: [
      {
        paragraphs: [
          "Each AIROBOTCAMSTUDIO capability is a layer. The interactive AI companion is what happens when those layers work as one — a character you can see, hear and talk with, that responds with voice, expression and movement in real time.",
        ],
      },
      {
        heading: "The intended architecture",
        stack: [
          "Photorealistic character",
          "Conversational AI",
          "Voice",
          "Real-time video",
          "Facial expressions",
          "Simulated emotional responses",
          "Natural movement",
          "Interactive AI companion experience",
        ],
      },
      {
        paragraphs: [
          "This represents the intended integrated product experience. AIROBOTCAMSTUDIO is not building another text chatbot — the goal is an experience you can see, hear, talk to and interact with.",
        ],
      },
      {
        heading: "The intended experience",
        paragraphs: [
          "SEE — a photorealistic character present on screen. HEAR — a natural voice with character. TALK — free-flowing conversation. INTERACT — expressions, movement and responses that react to you. The MVP feature pages describe each of these in detail.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "MVP currently in development. The integrated experience is the Level 1 goal on our roadmap.",
        ],
      },
    ],
    cta: { label: "Explore the MVP", href: "/#mvp" },
    back: { label: "Back to Technology", href: "/#technology" },
    navLabel: "Explore the Technology",
  },

  "/business-model/subscriptions": {
    tag: "Business Model · Proposed",
    title: "Subscriptions",
    subtitle: "Proposed recurring access to the interactive AI companion platform.",
    status: "Proposed",
    metaTitle: "Subscriptions — Proposed Business Model | AIROBOTCAMSTUDIO",
    metaDesc:
      "Subscriptions are a proposed future business model for AIROBOTCAMSTUDIO's interactive AI companion platform. Proposed only — commercial pricing has not been finalised.",
    sections: [
      {
        paragraphs: [
          "The most natural commercial model for an interactive AI companion platform is a subscription: members pay a recurring fee for ongoing access to the experience.",
          "For AIROBOTCAMSTUDIO, that would mean recurring access to real-time video interaction with photorealistic AI characters — the Level 1 digital product currently in development.",
        ],
      },
      {
        heading: "Commercial pricing",
        paragraphs: [
          "Commercial pricing has not been finalised. Any subscription structure, included usage and price points would be defined only after the MVP, informed by real user behaviour.",
        ],
      },
      {
        heading: "Why this model fits the product",
        bullets: [
          "Companionship is ongoing by nature, which suits recurring access rather than one-off purchases",
          "A subscription supports continuous improvement of characters, conversation and video quality",
          "Members would always use the current version of the platform as it develops",
          "Predictable access costs are simpler for users than per-session charging",
        ],
      },
      {
        heading: "How it connects to Level 1",
        paragraphs: [
          "Level 1 — the digital AI interaction platform now in development — is the product a subscription would pay for. No subscription exists today, and none can be purchased.",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [PROPOSED_MODEL_STATUS],
      },
    ],
    cta: { label: "Register Your Interest", href: "/#contact" },
    back: { label: "Back to Business Model", href: "/#business-model" },
    nav: BUSINESS_MODEL_NAV,
  },

  "/business-model/premium-interactive-experiences": {
    tag: "Business Model · Proposed",
    title: "Premium Interactive Experiences",
    subtitle: "Proposed enhanced real-time experiences for premium members.",
    status: "Proposed",
    metaTitle: "Premium Interactive Experiences — Proposed Business Model | AIROBOTCAMSTUDIO",
    metaDesc:
      "Premium interactive experiences are a proposed future tier of AIROBOTCAMSTUDIO's AI companion platform. Proposed only — no pricing set, no customers, no revenue today.",
    sections: [
      {
        paragraphs: [
          "Beyond a standard access tier, a future platform could offer premium interactive experiences: enhanced real-time sessions with higher-fidelity characters, longer or richer interactions, and presentation quality above the standard tier.",
          "Premium formats would sit alongside any standard access tier. Commercial pricing has not been finalised and would be defined only after the MVP.",
        ],
      },
      {
        heading: "What premium could mean",
        bullets: [
          "Higher-fidelity real-time video and character presentation",
          "Extended or uninterrupted interaction sessions",
          "Early access to new characters and capabilities as they are developed",
          "Enhanced personalisation within the platform's rules",
        ],
      },
      {
        heading: "How it connects to Level 1",
        paragraphs: [
          "Premium experiences would build directly on the Level 1 platform now in development — the same characters and real-time video technology, presented at a higher tier. No premium tier exists today.",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [PROPOSED_MODEL_STATUS],
      },
    ],
    cta: { label: "Register Your Interest", href: "/#contact" },
    back: { label: "Back to Business Model", href: "/#business-model" },
    nav: BUSINESS_MODEL_NAV,
  },

  "/business-model/live-ai-entertainment": {
    tag: "Business Model · Future",
    title: "Live AI Entertainment",
    subtitle: "Proposed scheduled live sessions and entertainment formats.",
    status: "Future",
    metaTitle: "Live AI Entertainment — Future Business Model | AIROBOTCAMSTUDIO",
    metaDesc:
      "Live AI entertainment is a future business model concept for AIROBOTCAMSTUDIO: scheduled live sessions and formats built around AI characters. Future only — nothing live today.",
    sections: [
      {
        paragraphs: [
          "A longer-term concept is live AI entertainment: scheduled, real-time formats built around AI characters — experiences an audience can watch and interact with at set times, rather than purely on demand.",
          "Live formats create shared moments: a session that happens once, at a specific time, with a character performing and responding in real time.",
        ],
      },
      {
        heading: "Why it is a later stage",
        bullets: [
          "Live formats demand a stable, proven real-time platform first",
          "They require the Level 1 experience to work reliably at scale",
          "Scheduling, moderation and production all add operational complexity",
          "It only makes sense once the core product has been validated with users",
        ],
      },
      {
        heading: "How it connects to Level 1",
        paragraphs: [
          "Live AI entertainment would sit on top of the same real-time video and character technology as Level 1. It is a future format idea, not a current product, and follows commercial validation of the core platform.",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [PROPOSED_MODEL_STATUS],
      },
    ],
    cta: { label: "Register Your Interest", href: "/#contact" },
    back: { label: "Back to Business Model", href: "/#business-model" },
    nav: BUSINESS_MODEL_NAV,
  },

  "/business-model/premium-ai-characters": {
    tag: "Business Model · Proposed",
    title: "Premium AI Characters",
    subtitle: "Proposed exclusive characters with distinct personalities and presentation.",
    status: "Proposed",
    metaTitle: "Premium AI Characters — Proposed Business Model | AIROBOTCAMSTUDIO",
    metaDesc:
      "Premium AI characters are a proposed future business model for AIROBOTCAMSTUDIO: exclusive characters with distinct identities. Proposed only — no pricing set, no customers today.",
    sections: [
      {
        paragraphs: [
          "Characters are the heart of the platform. A proposed commercial model is a roster of premium AI characters: exclusive digital humans with distinct appearances, personalities and presentation, available to premium members.",
          "Each character is designed as a consistent identity — appearance, voice, manner and personality that persist across interactions, so members can return to a character they know.",
        ],
      },
      {
        heading: "What makes a character premium",
        bullets: [
          "Exclusive characters not available on the standard tier",
          "Distinct, persistent identity — appearance, voice and personality",
          "Higher-effort art direction and presentation quality",
          "Character variety that gives members a reason to stay subscribed",
        ],
      },
      {
        heading: "How it connects to Level 1",
        paragraphs: [
          "The photorealistic character technology behind Level 1 — in development now — is exactly what premium characters would be built with. The concept characters shown on this website are AI-generated and fictional, and illustrate the intended direction.",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [PROPOSED_MODEL_STATUS],
      },
    ],
    cta: { label: "Register Your Interest", href: "/#contact" },
    back: { label: "Back to Business Model", href: "/#business-model" },
    nav: BUSINESS_MODEL_NAV,
  },

  "/business-model/future-b2b": {
    tag: "Business Model · Future",
    title: "Future B2B Opportunities",
    subtitle: "Licensing and partnership models over the longer term.",
    status: "Future",
    metaTitle: "Future B2B Opportunities | AIROBOTCAMSTUDIO",
    metaDesc:
      "Future B2B opportunities are a long-term concept for AIROBOTCAMSTUDIO: potential licensing of AI character technology. Future only — no signed partners, no agreements in place.",
    sections: [
      {
        paragraphs: [
          "Over the longer term, the technology behind the platform — photorealistic AI characters, real-time video interaction, conversational intelligence — could have value beyond a direct-to-consumer product.",
          "Possible future directions include licensing character technology to other businesses, or partnerships where interactive AI characters serve entertainment, hospitality or brand experiences.",
        ],
      },
      {
        heading: "Why B2B comes later",
        bullets: [
          "The technology must first be proven in our own product",
          "Businesses adopt technology that already works for real users",
          "Licensing requires mature, reliable infrastructure and clear ownership",
          "Our focus today is the Level 1 digital platform, not enterprise sales",
        ],
      },
      {
        heading: "Honest status",
        paragraphs: [
          PROPOSED_MODEL_STATUS,
          "Specifically for B2B: AIROBOTCAMSTUDIO has no signed B2B partners, no partnership agreements and no licensing arrangements in place or in negotiation.",
        ],
      },
    ],
    cta: { label: "Contact the Team", href: "/#contact" },
    back: { label: "Back to Business Model", href: "/#business-model" },
    nav: BUSINESS_MODEL_NAV,
  },

  "/market/artificial-intelligence": {
    tag: "Market Context",
    title: "Artificial Intelligence",
    subtitle: "Why AI is the foundation of everything we are building.",
    status: "In development",
    metaTitle: "Artificial Intelligence — Market Context | AIROBOTCAMSTUDIO",
    metaDesc:
      "Why artificial intelligence matters to AIROBOTCAMSTUDIO: conversational AI, voice and character technology as the foundation of the Level 1 digital platform now in development.",
    sections: [
      {
        heading: "Why this category is relevant",
        paragraphs: [
          "Artificial intelligence has moved from research labs into everyday products. Conversation, voice and visual generation have reached the point where an AI character can hold a plausible, flowing interaction — something that simply was not possible a few years ago.",
          "This progress is what makes a real-time AI companion platform conceivable at all. Every layer of the intended product — understanding, speaking, expressing — is an AI capability.",
        ],
      },
      {
        heading: "How it connects to AIROBOTCAMSTUDIO",
        paragraphs: [
          "AIROBOTCAMSTUDIO is not a general-purpose AI company. We apply existing and maturing AI capabilities to one specific experience: a photorealistic character you can see, hear and talk with in real time.",
          "Our work is in the combination — bringing conversation, voice, expression, movement and video together into a single coherent character, presented honestly as simulation rather than claimed as consciousness.",
        ],
      },
      {
        heading: "Relation to the Level 1 digital product",
        paragraphs: [
          "Level 1 — the MVP currently in development — is where this category becomes a product: conversational AI drives the dialogue, voice AI gives the character speech, and visual AI presents the character on screen. The technology pages describe each layer in detail.",
        ],
      },
    ],
    note: NO_MARKET_STATS_NOTE,
    cta: { label: "Explore the Technology", href: "/#technology" },
    back: { label: "Back to Market", href: "/#market" },
    nav: MARKET_NAV,
  },

  "/market/interactive-entertainment": {
    tag: "Market Context",
    title: "Interactive Entertainment",
    subtitle: "Why entertainment is moving from passive viewing to participation.",
    status: "In development",
    metaTitle: "Interactive Entertainment — Market Context | AIROBOTCAMSTUDIO",
    metaDesc:
      "Why interactive entertainment matters to AIROBOTCAMSTUDIO: entertainment is shifting from passive content to participation, and the Level 1 platform is being built as an interactive experience.",
    sections: [
      {
        heading: "Why this category is relevant",
        paragraphs: [
          "Entertainment has steadily shifted from passive to participatory. Audiences increasingly expect to interact — to influence, respond and take part — rather than simply watch.",
          "Interactive formats hold attention differently from static content: the experience responds to you, which makes it personal in a way a video or a page of text cannot be.",
        ],
      },
      {
        heading: "How it connects to AIROBOTCAMSTUDIO",
        paragraphs: [
          "AIROBOTCAMSTUDIO positions its product as interactive entertainment, not a utility or a chatbot. The intended experience is closer to entertainment than to software: a character with presence, personality and performance that you engage with in real time.",
        ],
      },
      {
        heading: "Relation to the Level 1 digital product",
        paragraphs: [
          "Level 1 — in development now — is designed as an interactive entertainment experience first: real-time videochat with photorealistic AI characters who respond with voice, expression and movement. Interactivity is the product, not a feature added to it.",
        ],
      },
    ],
    note: NO_MARKET_STATS_NOTE,
    cta: { label: "Explore the Technology", href: "/#technology" },
    back: { label: "Back to Market", href: "/#market" },
    nav: MARKET_NAV,
  },

  "/market/real-time-video": {
    tag: "Market Context",
    title: "Real-Time Video",
    subtitle: "Why face-to-face presence changes digital interaction.",
    status: "In development",
    metaTitle: "Real-Time Video — Market Context | AIROBOTCAMSTUDIO",
    metaDesc:
      "Why real-time video matters to AIROBOTCAMSTUDIO: live, face-to-face interaction is the most natural communication medium, and it is the core of the Level 1 product in development.",
    sections: [
      {
        heading: "Why this category is relevant",
        paragraphs: [
          "Video is the richest digital communication medium people use every day — calls, streams and live formats have made real-time face-to-face interaction the default expectation online.",
          "Most AI companions today remain text or voice only. Real-time video is where presence actually happens: seeing a character react as you speak is a categorically different experience from reading a reply.",
        ],
      },
      {
        heading: "How it connects to AIROBOTCAMSTUDIO",
        paragraphs: [
          "Real-time video is not an add-on for AIROBOTCAMSTUDIO — it is the core of the product. The entire platform is being designed around a live, video-call style interaction where the character is present on screen and responds in the moment.",
        ],
      },
      {
        heading: "Relation to the Level 1 digital product",
        paragraphs: [
          "Level 1 — the MVP currently in development — is precisely this: a real-time AI videochat experience. Every other layer (voice, conversation, expression, movement) exists to make that live video interaction feel natural.",
        ],
      },
    ],
    note: NO_MARKET_STATS_NOTE,
    cta: { label: "Explore the Technology", href: "/#technology" },
    back: { label: "Back to Market", href: "/#market" },
    nav: MARKET_NAV,
  },

  "/market/virtual-companions": {
    tag: "Market Context",
    title: "Virtual Companions",
    subtitle: "Why AI companionship is emerging as its own category.",
    status: "In development",
    metaTitle: "Virtual Companions — Market Context | AIROBOTCAMSTUDIO",
    metaDesc:
      "Why virtual companions matter to AIROBOTCAMSTUDIO: AI companionship is an emerging category, and the Level 1 platform aims to make it visual and real-time rather than text-based.",
    sections: [
      {
        heading: "Why this category is relevant",
        paragraphs: [
          "AI companionship has emerged as a recognisable category in its own right. People already spend time with AI characters for conversation, entertainment and company — mostly through text and static images.",
          "The category's limitation today is presence: a companion you can only read is a conversation, not an encounter. The opportunity is in making companionship visual, expressive and real-time.",
        ],
      },
      {
        heading: "How it connects to AIROBOTCAMSTUDIO",
        paragraphs: [
          "AIROBOTCAMSTUDIO is building directly in this category, with a clear point of difference: photorealistic characters, live video, natural voice and expressive response rather than a text box. Our characters are presented honestly as simulated personalities — engaging by design, without claims of consciousness or genuine emotion.",
        ],
      },
      {
        heading: "Relation to the Level 1 digital product",
        paragraphs: [
          "Level 1 — in development now — is a virtual companion experience: one photorealistic AI character, in a real-time videochat, with conversation, expression and movement. It is the foundation on which any wider companion platform would be built.",
        ],
      },
    ],
    note: NO_MARKET_STATS_NOTE,
    cta: { label: "Explore the Technology", href: "/#technology" },
    back: { label: "Back to Market", href: "/#market" },
    nav: MARKET_NAV,
  },
};
